<template>
  <div class="map-page">
    <HeaderComponent class="app-header" />

    <div class="map-container">
      <div id="map"></div>

      <div class="overlay-panel">
        <LocationSearchBox @select="onLocationSelect" />
        <SpotSearchBox v-model="searchQuery" />
        <button type="button" class="tag-toggle" @click="showTagFilter = !showTagFilter">
          {{ tagToggleLabel }}
        </button>
        <TagFilterBar v-if="showTagFilter" v-model="activeTags" />
      </div>

      <div class="hint-box" v-if="!showModal">
        📍 {{ isMobile ? 'Press and hold the map to add a spot' : 'Right-click the map to add a spot' }}
      </div>
      <div class="location-button" @click="goToUserLocation" v-if="!showModal">
        📍 My Location
      </div>
    </div>

    <SpotEditorModal
      v-if="showModal"
      :is-mobile="isMobile"
      :read-only="viewingExisting"
      :spot="activeSpot"
      @save="handleSave"
      @cancel="handleCancel"
      @close="handleCancel"
      @spot-updated="handleSpotUpdated"
    />
  </div>
</template>

<script>
import { onMounted, ref, computed, watch } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import icon2x from 'leaflet/dist/images/marker-icon-2x.png';
import HeaderComponent from './HeaderComponent.vue';
import LocationSearchBox from './LocationSearchBox.vue';
import SpotSearchBox from './SpotSearchBox.vue';
import TagFilterBar from './TagFilterBar.vue';
import SpotEditorModal from './SpotEditorModal.vue';

// Leaflet's default marker icon URLs assume a plain <img>-relative path, which breaks once
// webpack fingerprints/moves the images. Point it at the bundled asset URLs instead.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: icon2x,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

const draftIcon = L.divIcon({
  className: 'draft-pin-icon',
  html: '<div class="pin pin-red"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

// Zoom level used once we're centering on somewhere specific (user location, a search result).
const LOCAL_ZOOM = 14;

export default {
  name: 'MapComponent',
  components: { HeaderComponent, LocationSearchBox, SpotSearchBox, TagFilterBar, SpotEditorModal },
  setup() {
    const map = ref(null);
    const spotsLayer = ref(null);
    const draftMarker = ref(null);
    const userLocation = ref(null);
    const spots = ref([]);
    const activeTags = ref([]);
    const showTagFilter = ref(false);
    const searchQuery = ref('');
    const tagToggleLabel = computed(() =>
      activeTags.value.length > 0 ? `Tags (${activeTags.value.length}) ${showTagFilter.value ? '▴' : '▾'}` : `Filter by tag ${showTagFilter.value ? '▴' : '▾'}`
    );
    const showModal = ref(false);
    const viewingExisting = ref(false);
    const activeSpot = ref({ name: '', notes: '', tags: [], createdAt: null });
    const isMobile = computed(() => window.innerWidth <= 768);

    const visibleSpots = computed(() => {
      let result = spots.value;
      if (activeTags.value.length > 0) {
        result = result.filter((spot) => (spot.tags || []).some((t) => activeTags.value.includes(t)));
      }
      const query = searchQuery.value.trim().toLowerCase();
      if (query.length > 0) {
        result = result.filter(
          (spot) =>
            (spot.name || '').toLowerCase().includes(query) || (spot.notes || '').toLowerCase().includes(query)
        );
      }
      return result;
    });

    const renderSpots = () => {
      spotsLayer.value.clearLayers();
      visibleSpots.value.forEach((spot) => {
        const marker = L.marker([spot.latitude, spot.longitude]);
        marker.on('click', (e) => {
          // Marker clicks bubble up to the map's own click handler by default, which
          // would otherwise immediately close the spot this click just opened (see the
          // map's 'click' handler below). Stop it here instead of guarding there, so the
          // map handler can stay a simple "close whatever's open."
          L.DomEvent.stopPropagation(e);
          clearDraftMarker();
          activeSpot.value = spot;
          viewingExisting.value = true;
          showModal.value = true;
        });
        marker.addTo(spotsLayer.value);
      });
    };

    const loadSpots = async () => {
      try {
        const response = await fetch('/api/spots');
        spots.value = await response.json();
        renderSpots();
      } catch (error) {
        console.error('Error fetching spots:', error);
      }
    };

    const clearDraftMarker = () => {
      if (draftMarker.value) {
        map.value.removeLayer(draftMarker.value);
        draftMarker.value = null;
      }
    };

    const openDraftAt = (latlng) => {
      if (showModal.value) return;
      clearDraftMarker();
      draftMarker.value = L.marker(latlng, { draggable: true, icon: draftIcon }).addTo(map.value);
      activeSpot.value = { name: '', notes: '', tags: [], createdAt: null };
      viewingExisting.value = false;
      showModal.value = true;
    };

    const handleSave = async ({ name, notes, tags, website }) => {
      if (!draftMarker.value) return;
      const { lat, lng } = draftMarker.value.getLatLng();
      try {
        const response = await fetch('/api/spots', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: lat, longitude: lng, name, notes, tags, website }),
        });
        if (!response.ok) {
          console.error('Error saving spot:', await response.text());
          return;
        }
        clearDraftMarker();
        showModal.value = false;
        await loadSpots();
      } catch (error) {
        console.error('Error saving spot:', error);
      }
    };

    const handleCancel = () => {
      clearDraftMarker();
      showModal.value = false;
    };

    const handleSpotUpdated = (updatedSpot) => {
      const index = spots.value.findIndex((s) => s.id === updatedSpot.id);
      if (index !== -1) {
        spots.value[index] = updatedSpot;
      }
      activeSpot.value = updatedSpot;
    };

    const getUserLocation = () => {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          userLocation.value = [position.coords.latitude, position.coords.longitude];
          map.value.setView(userLocation.value, LOCAL_ZOOM);
        },
        () => console.log('Unable to retrieve location')
      );
    };

    const goToUserLocation = () => {
      if (userLocation.value) {
        map.value.flyTo(userLocation.value, LOCAL_ZOOM);
      } else {
        getUserLocation();
      }
    };

    const onLocationSelect = ({ lat, lng, bounds }) => {
      // Nominatim returns a bounding box for every result, not just a point — for a city
      // or a business address that's a tiny box (so this behaves like the old fixed-zoom
      // flyTo, capped at the same LOCAL_ZOOM), but for a country or region it's the actual
      // extent. Flying to a fixed zoom regardless of that meant searching "Mexico" landed
      // on some arbitrary point within it at street-level zoom instead of framing the
      // whole country.
      if (bounds) {
        map.value.flyToBounds(bounds, { maxZoom: LOCAL_ZOOM });
      } else {
        map.value.flyTo([lat, lng], LOCAL_ZOOM);
      }
    };

    onMounted(() => {
      // worldCopyJump: seamlessly wraps panning across repeated copies of the world at
      // low zoom, instead of stopping dead at the antimeridian.
      map.value = L.map('map', { worldCopyJump: true });

      // A fixed center/zoom shows a narrower slice of longitude on a tall narrow (mobile
      // portrait) container than on a wide desktop one at the same zoom — on a phone that
      // meant the default view was a strip through Europe/Africa (where the fixed center
      // happened to sit) instead of the whole world. Fitting to bounds instead computes
      // center and zoom from the actual container's aspect ratio.
      //
      // Deliberately not the full 360° of longitude (fitWorld()/[-180,180]): forcing a
      // tall narrow phone screen to fit the *entire* world width requires zooming out so
      // far that the fitted height collapses to a small band, leaving large gray letterbox
      // bars above and below. This range (Pacific to the Middle East) covers the Americas
      // through Europe/Africa — where the seeded content actually is — on any aspect ratio
      // without that extreme zoom-out; the world still wraps/repeats on pan either way.
      map.value.fitBounds([
        [-55, -170],
        [72, 60],
      ]);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map.value);

      spotsLayer.value = L.layerGroup().addTo(map.value);
      watch(visibleSpots, renderSpots);

      // Leaflet's Tap handler fires a synthetic 'contextmenu' on a long touch-and-hold, so
      // this single handler covers both desktop right-click and mobile long-press.
      map.value.on('contextmenu', (e) => openDraftAt(e.latlng));

      // A plain click on open map (not on a marker — those stop this from firing, see
      // above) closes whatever's currently open: a viewed spot, or an in-progress draft.
      map.value.on('click', () => {
        if (showModal.value) handleCancel();
      });

      loadSpots();
    });

    return {
      showModal,
      viewingExisting,
      activeSpot,
      activeTags,
      showTagFilter,
      searchQuery,
      tagToggleLabel,
      goToUserLocation,
      handleSave,
      handleCancel,
      handleSpotUpdated,
      onLocationSelect,
      isMobile,
    };
  },
};
</script>

<style scoped>
.map-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
}

.app-header {
  flex-shrink: 0;
}

.map-container {
  flex: 1;
  position: relative;
  min-height: 0;
}

#map {
  width: 100%;
  height: 100%;
}

.overlay-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  width: 260px;
  max-width: calc(100% - 20px);
  max-height: calc(100% - 80px);
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tag-toggle {
  align-self: flex-start;
  background: none;
  border: none;
  color: #2d7a3a;
  font-size: 13px;
  padding: 2px 0;
  cursor: pointer;
}

.location-button {
  position: absolute;
  bottom: 20px;
  left: 10px;
  background-color: white;
  border: 1px solid #ccc;
  padding: 10px;
  cursor: pointer;
  z-index: 1000;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.hint-box {
  position: absolute;
  bottom: 20px;
  right: 10px;
  max-width: 220px;
  background-color: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: #333;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.3;
  z-index: 1000;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  pointer-events: none;
}

/* Side-by-side bottom corners work down to a point, but "My Location" plus this box's
   text is wider than many phones' screens — measured overlapping at 375px. Stack the hint
   above both bottom buttons instead of beside them once there isn't room, rather than
   trying to guess a width that happens to fit every device. */
@media (max-width: 480px) {
  .hint-box {
    bottom: 68px;
    left: 10px;
    right: 10px;
    max-width: none;
    text-align: center;
  }
}
</style>

<style>
/* Unscoped: targets a divIcon's innerHTML, which scoped styles can't reach. */
.pin {
  width: 20px;
  height: 20px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  border: 2px solid white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
}

.pin-red {
  background-color: #e33;
}
</style>
